import { useCallback, useEffect, useMemo, useState, useSyncExternalStore } from 'react';
import { Outlet, useNavigate, useParams } from 'react-router-dom';
import { listBoards } from '../../api/boards';
import { BoardsContext, type BoardsContextValue, type BoardsState } from './boards-context';

/** 목록 화면과 같은 모양의 좁은 오류 타입 — 서버 문구가 있으면 그대로 보여 준다. */
type ApiError = { response?: { status?: number; data?: { message?: string } }; message?: string };
import { BoardTreeCard } from './list/BoardTreeCard';
import { BoardV1CreateDialog } from './list/BoardV1CreateDialog';
import { boardListPath, boardTrashPath, parseNumericId } from './board-v1-url';
import { getBoardTrashSupported, subscribeBoardTrashSupport } from './board-trash-support';
import './BoardShell.css';

/**
 * 게시판 셸 (2026-09-17 · 사용자 지시) — 좌 240px **게시판 트리** + 우 화면(`<Outlet/>`).
 *
 * ## 왜 셸인가
 *
 * 종전에는 트리가 `BoardListScreen` 의 본문 격자 안에 있는 «카드»였다(관제 Q-F · 272px).
 * 그래서 두 가지가 사용자 지적으로 드러났다:
 *   ① 목록에서 트리가 KPI 줄 **아래 왼쪽**에 작게 놓여 «좌측 메뉴»로 보이지 않았다.
 *   ② **글 상세 · 글쓰기 · 설정에는 트리가 아예 없었다** — 화면을 옮기면 좌측 메뉴가 사라졌다.
 * 셸로 올리면 좌측 메뉴는 `<Outlet/>` 밖에 있으므로 **모든 하위 화면에서 그대로 선다**.
 *
 * ★ 폭 240 은 결재·근태·인사·AI 셸과 **같은 값**이다(집안 관례). 272 는 카드였을 때의 값이라 쓰지 않는다.
 * ★ 목록은 **셸이 혼자 읽는다**(`boards-context.tsx`) — 트리와 본문이 같은 값을 쓴다.
 *   화면 이동마다 다시 읽지 않는다(게시판 목록은 이동으로 바뀌지 않는다). 다시 세야 할 일
 *   —「모두 읽음」· 휴지통 되살리기 · 새 게시판 — 이 생기면 화면이 `recount()` 를 부르고,
 *   그때 **트리와 본문이 함께** 새 값을 받는다.
 */
export function BoardShell() {
  const navigate = useNavigate();
  const params = useParams();

  const [state, setState] = useState<BoardsState>({ status: 'loading' });
  const [version, setVersion] = useState(0);
  const [creating, setCreating] = useState(false);
  /** 주소에 게시판 id 가 없는 화면(글 상세)이 알려 주는 «지금 보는 게시판». */
  const [viewingBoardId, setViewingBoardId] = useState<string | null>(null);
  /** 서버가 휴지통을 모르면 단추를 감춘다 — 누를 수 없는 단추를 남기지 않는다(종전 판단 그대로). */
  const trashSupported = useSyncExternalStore(subscribeBoardTrashSupport, getBoardTrashSupported, getBoardTrashSupported);

  useEffect(() => {
    let cancelled = false;
    listBoards()
      .then((r) => { if (!cancelled) setState({ status: 'ready', boards: r.boards, canCreate: r.canCreate }); })
      .catch((e: unknown) => {
        if (cancelled) return;
        // 이미 한 번 받았으면 옛 목록을 그대로 둔다(배지가 조금 늦는 것이 화면이 비는 것보다 낫다).
        setState((prev) => (prev.status === 'ready'
          ? prev
          : { status: 'error', message: (e as ApiError)?.response?.data?.message ?? (e as ApiError)?.message ?? 'unknown' }));
      });
    return () => { cancelled = true; };
  }, [version]);

  /**
   * 지금 보고 있는 게시판. 목록·휴지통·글쓰기·설정은 주소에 `:boardId` 가 있다.
   * **글 상세(`/board/post/:postId`)에는 없어서**, 그 화면이 글을 받아 본 뒤
   * `setViewingBoardId` 로 알려 준다 — 그래야 좌측 메뉴가 「어디에 있는지」를 그린다.
   */
  const activeId = parseNumericId(params.boardId) ?? viewingBoardId;

  const openTrash = useCallback(() => {
    if (activeId) navigate(boardTrashPath(activeId));
  }, [activeId, navigate]);

  const recount = useCallback(() => setVersion((v) => v + 1), []);
  const ctx = useMemo<BoardsContextValue>(
    () => ({ state, recount, version, setViewingBoardId }),
    [state, recount, version],
  );

  const boards = state.status === 'ready' ? state.boards : [];
  const canCreate = state.status === 'ready' && state.canCreate;

  return (
    <div className="bvshell">
      <div className="bvshell__nav">
        <BoardTreeCard
          variant="shell"
          boards={boards}
          activeId={activeId}
          canCreate={canCreate}
          showTrash={trashSupported && !!activeId}
          onSelect={(id) => navigate(boardListPath(id))}
          onCreate={() => setCreating(true)}
          onOpenTrash={openTrash}
        />
      </div>

      <div className="bvshell__body">
        <BoardsContext.Provider value={ctx}>
          <Outlet />
        </BoardsContext.Provider>
      </div>

      {creating && (
        <BoardV1CreateDialog
          onClose={() => setCreating(false)}
          onCreated={(b) => {
            setCreating(false);
            recount();
            navigate(boardListPath(b.id));
          }}
        />
      )}
    </div>
  );
}
